import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useStyles } from "../../../Theme/Style";
import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useNotification } from "../../../Contexts/GlobalNotificationContext";
import {
  useGetTrimDetailsQuery,
  useSubmitTrimDetailsMutation,
} from "../../../services/api/production-order";
// ⬇️ NEW: trim type API
import { useGetTrimTypeQuery } from "../../../services/api/trim-types";

const TrimDetailsComponent = ({ prodId }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const { data: trimData, isLoading: isLoadingTrims } = useGetTrimDetailsQuery({ id: prodId });
  const [submitTrimDetails] = useSubmitTrimDetailsMutation();

  // ⬇️ NEW: load all trim types
  const { data: trimTypeResp, isLoading: isLoadingTrimTypes } = useGetTrimTypeQuery();

  // Normalize possible shapes from API (array, {data:[]}, {trim_types:[]})
  const allTrimTypes = useMemo(() => {
    if (!trimTypeResp) return [];
    if (Array.isArray(trimTypeResp)) return trimTypeResp;
    if (Array.isArray(trimTypeResp?.data)) return trimTypeResp.data;
    if (Array.isArray(trimTypeResp?.trim_types)) return trimTypeResp.trim_types;
    return [];
  }, [trimTypeResp]);

  const [trimItems, setTrimItems] = useState([]);

  useEffect(() => {
    if (trimData?.trims?.length) {
      const initialItems = trimData.trims.map((t) => ({
        item: t.item || null,
        trimType: t.trim_type || null,
        color: t.color || null,
        position: t.position || null,
      }));
      setTrimItems(initialItems);
    } else {
      // ensure at least one row to start
      setTrimItems([{ item: null, trimType: null, color: null, position: null }]);
    }
  }, [trimData]);

  const addNewRow = () => {
    setTrimItems((prev) => [...prev, { item: null, trimType: null, color: null, position: null }]);
  };

  const handleChange = (event, fieldName, index, value) => {
    setTrimItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [fieldName]: value };
      return copy;
    });
  };

  const deleteRow = (index) => {
    setTrimItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async () => {
    try {
      const isValid = trimItems.every(
        (item) =>
          item.item?.id &&
          item.trimType?.id &&     // ⬅️ now must be an object with id
          item.color?.id &&
          item.position?.id
      );
      if (!isValid) {
        throw new Error("Please fill out all fields in each row.");
      }
      await submitTrimDetails({
        production_order_id: prodId,
        trims: trimItems,
      }).unwrap();
      addNotification({ message: "Trim details submitted successfully!", type: "success" });
    } catch (error) {
      addNotification({ message: error?.message || "Failed to submit trim details.", type: "error" });
    }
  };

  const loading = isLoadingTrims || isLoadingTrimTypes;

  const labelForItem = (o) => (o ? `${o.name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForColor = (o) => (o ? `${o.english_name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForPosition = (o) => (o ? `${o.name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForTrimType = (o) =>
    o ? `${o.name || o.technical_name || ""}${o.chinese_name ? " - " + o.chinese_name : ""}`.trim() : "";

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        aria-controls="trim-details-content"
        id="trim-details-header"
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={4}>Trim Details</Grid>
          <Grid item xs={4}>修剪细节</Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        {/* Table header */}
        <Box className={classes.tableHeader}>
          <Grid container>
            <Grid item xs={3}>
              <Typography>Item</Typography>
              <Typography>物品</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>Trim Type</Typography>
              <Typography>修剪类型</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>Color</Typography>
              <Typography>颜色</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>Position</Typography>
              <Typography>位置</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Render each trim detail row */}
        {trimItems.map((row, index) => (
          <Box key={index} className={classes.row} sx={{ mt: 1 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Item Selection */}
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={trimData?.items || []}
                    getOptionLabel={labelForItem}
                    value={row.item}
                    loading={loading}
                    onChange={(event, value) => handleChange(event, "item", index, value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Item" variant="standard" />
                    )}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  />
                </FormControl>
              </Grid>

              {/* ⬇️ NEW: Trim Type Selection (Autocomplete) */}
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={allTrimTypes}
                    getOptionLabel={labelForTrimType}
                    value={
                      row.trimType
                        ? allTrimTypes.find((t) => t?.id === row.trimType?.id) || row.trimType
                        : null
                    }
                    loading={loading}
                    onChange={(event, value) => handleChange(event, "trimType", index, value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Trim Type" variant="standard" />
                    )}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  />
                </FormControl>
              </Grid>

              {/* Color Selection */}
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={trimData?.colors || []}
                    getOptionLabel={labelForColor}
                    value={row.color}
                    loading={loading}
                    onChange={(event, value) => handleChange(event, "color", index, value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Color" variant="standard" />
                    )}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  />
                </FormControl>
              </Grid>

              {/* Position Selection */}
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={trimData?.positions || []}
                    getOptionLabel={labelForPosition}
                    value={row.position}
                    loading={loading}
                    onChange={(event, value) => handleChange(event, "position", index, value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Position" variant="standard" />
                    )}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  />
                </FormControl>
              </Grid>

              {/* Remove Row Button */}
              <Grid item xs={12} sx={{ textAlign: "right" }}>
                <Button
                  onClick={() => deleteRow(index)}
                  disabled={trimItems.length === 1}
                  startIcon={<RemoveCircleOutline />}
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}

        {/* Add New Row and Save buttons */}
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={addNewRow} startIcon={<AddCircleOutline />} disabled={loading}>
            Add New Row
          </Button>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={loading || !trimItems.length}>
            Save
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default TrimDetailsComponent;
